import { match } from 'ts-pattern'
import type { CorrelationSummary, ExploitChainsPhase, ExploitChainsStatus } from '../../utils/api-client/api-schemas.ts'
import {
  CandidateVerdictDetailSchema,
  CandidateVerifyingDetailSchema,
  CandidatesMergedDetailSchema,
  ChainsGroundedDetailSchema,
  type ExploitChainsStreamEvent,
  IndexBuiltDetailSchema,
  InputsLoadedDetailSchema,
  LensFailedDetailSchema,
  LensProposedDetailSchema,
  LensStartedDetailSchema,
  type Verdict,
  VerdictSchema,
} from '../../utils/api-client/sse-schemas.ts'

/**
 * One view of a correlation run, whether it is being watched live or read back after the fact.
 *
 * The live stream and the completed `correlation` summary describe the same run in two shapes;
 * folding both into this one state means the canvas, the hypothesis log and the summary panel
 * render from a single model and the empty state is literally the last frame of the animation.
 */

export type RunTool = {
  tool: string
  kind: string
  count: number
  findingIds: string[]
}

export type RunFinding = {
  findingId: string
  rule: string
  path: string
  title?: string
  tool?: string
}

export type HypothesisVerdict = 'pending' | 'verifying' | Verdict

export type Hypothesis = {
  id: string
  lenses: string[]
  findingIds: string[]
  findings: RunFinding[]
  hypothesis: string
  verdict: HypothesisVerdict
  reason?: string
  title?: string
  severity?: string
}

export type LensProgress = {
  lens: string
  state: 'running' | 'done' | 'failed'
  proposed: number
}

export type CandidatePool = {
  raw: number
  unique: number
  merged: number
}

export type RunGrounding = {
  chainsIn: number
  chainsOut: number
  referencesRepaired: number
  referencesDropped: number
}

export type RunState = {
  phase: ExploitChainsPhase
  status: ExploitChainsStatus
  tools?: RunTool[]
  totalFindings?: number
  indexed?: number
  droppedIds: string[]
  lenses: LensProgress[]
  pool?: CandidatePool
  hypotheses: Hypothesis[]
  grounding?: RunGrounding
  threatModelGeneratedAt?: string
  /** Wall-clock seconds the correlating phase took, once the run has finished. */
  correlationSeconds?: number
  startedAt?: string
  lastEventAt?: string
}

export const EMPTY_RUN: RunState = {
  phase: 'queued',
  status: 'processing',
  droppedIds: [],
  lenses: [],
  hypotheses: [],
}

const upsertLens = (lenses: LensProgress[], next: LensProgress): LensProgress[] =>
  lenses.some(lens => lens.lens === next.lens)
    ? lenses.map(lens => (lens.lens === next.lens ? next : lens))
    : [...lenses, next]

const updateHypothesis = (
  hypotheses: Hypothesis[],
  candidateId: string,
  update: (hypothesis: Hypothesis) => Hypothesis
): Hypothesis[] => hypotheses.map(hypothesis => (hypothesis.id === candidateId ? update(hypothesis) : hypothesis))

/**
 * Applies one stream event. Unknown or malformed `detail` payloads only advance the phase and
 * status, so a stream from a newer service degrades to the stepper it always drove.
 */
export const applyStreamEvent = (state: RunState, event: ExploitChainsStreamEvent): RunState => {
  const base: RunState = {
    ...state,
    phase: event.phase,
    status: event.status,
    startedAt: state.startedAt ?? event.timestamp,
    lastEventAt: event.timestamp,
  }
  if (!event.event) {
    return base
  }
  return match(event.event)
    .with('inputs_loaded', () => {
      const parsed = InputsLoadedDetailSchema.safeParse(event.detail)
      if (!parsed.success) {
        return base
      }
      return {
        ...base,
        totalFindings: parsed.data.findings.total,
        tools: parsed.data.findings.by_tool.map(tool => ({
          tool: tool.tool,
          kind: tool.kind,
          count: tool.count,
          findingIds: tool.finding_ids,
        })),
        threatModelGeneratedAt: parsed.data.threat_model_generated_at ?? undefined,
      }
    })
    .with('index_built', () => {
      const parsed = IndexBuiltDetailSchema.safeParse(event.detail)
      return parsed.success ? { ...base, indexed: parsed.data.indexed, droppedIds: parsed.data.dropped_ids } : base
    })
    .with('lens_started', () => {
      const parsed = LensStartedDetailSchema.safeParse(event.detail)
      return parsed.success
        ? { ...base, lenses: upsertLens(base.lenses, { lens: parsed.data.lens, state: 'running', proposed: 0 }) }
        : base
    })
    .with('lens_proposed', () => {
      const parsed = LensProposedDetailSchema.safeParse(event.detail)
      return parsed.success
        ? {
            ...base,
            lenses: upsertLens(base.lenses, { lens: parsed.data.lens, state: 'done', proposed: parsed.data.proposed }),
          }
        : base
    })
    .with('lens_failed', () => {
      const parsed = LensFailedDetailSchema.safeParse(event.detail)
      return parsed.success
        ? { ...base, lenses: upsertLens(base.lenses, { lens: parsed.data.lens, state: 'failed', proposed: 0 }) }
        : base
    })
    .with('candidates_merged', () => {
      const parsed = CandidatesMergedDetailSchema.safeParse(event.detail)
      if (!parsed.success) {
        return base
      }
      return {
        ...base,
        pool: { raw: parsed.data.raw, unique: parsed.data.unique, merged: parsed.data.merged },
        hypotheses: parsed.data.candidates.map(candidate => ({
          id: candidate.id,
          lenses: candidate.lenses,
          findingIds: candidate.finding_ids,
          findings: candidate.findings.map(finding => ({
            findingId: finding.finding_id,
            rule: finding.rule,
            path: finding.path,
            title: finding.title ?? undefined,
            tool: finding.tool ?? undefined,
          })),
          hypothesis: candidate.hypothesis,
          verdict: 'pending' as const,
        })),
      }
    })
    .with('candidate_verifying', () => {
      const parsed = CandidateVerifyingDetailSchema.safeParse(event.detail)
      return parsed.success
        ? {
            ...base,
            hypotheses: updateHypothesis(base.hypotheses, parsed.data.candidate_id, hypothesis => ({
              ...hypothesis,
              verdict: 'verifying' as const,
            })),
          }
        : base
    })
    .with('candidate_verdict', () => {
      const parsed = CandidateVerdictDetailSchema.safeParse(event.detail)
      if (!parsed.success) {
        return base
      }
      const { verdict, reason, title, severity } = parsed.data
      return {
        ...base,
        hypotheses: updateHypothesis(base.hypotheses, parsed.data.candidate_id, hypothesis => ({
          ...hypothesis,
          verdict,
          reason,
          title: title ?? undefined,
          severity: severity ?? undefined,
        })),
      }
    })
    .with('chains_grounded', () => {
      const parsed = ChainsGroundedDetailSchema.safeParse(event.detail)
      return parsed.success
        ? {
            ...base,
            grounding: {
              chainsIn: parsed.data.chains_in,
              chainsOut: parsed.data.chains_out,
              referencesRepaired: parsed.data.references_repaired,
              referencesDropped: parsed.data.references_dropped,
            },
          }
        : base
    })
    .exhaustive()
}

export const runStateFromStream = (events: readonly ExploitChainsStreamEvent[]): RunState =>
  events.reduce(applyStreamEvent, EMPTY_RUN)

/**
 * The completed run, read back from its stored summary. Every hypothesis already has its verdict,
 * so this is the resting frame the live view converges to.
 */
// The wire keeps `verdict` open so a value added upstream cannot fail the response; anything this
// build does not know is shown as an errored verification rather than invented.
const toVerdict = (value: string): Verdict => VerdictSchema.catch('errored').parse(value)

export const runStateFromSummary = ({
  summary,
  phase,
  status,
}: {
  summary: CorrelationSummary
  phase: ExploitChainsPhase
  status: ExploitChainsStatus
}): RunState => {
  const tools = summary.findings.byTool.map(tool => ({
    tool: tool.tool,
    kind: tool.kind,
    count: tool.count,
    findingIds: tool.findingIds,
  }))
  return {
    phase,
    status,
    tools,
    totalFindings: summary.findings.dispatched,
    indexed: summary.findings.indexed,
    // The summary keeps only the count of what never fit; the ids are not stored with it, so the
    // canvas cannot hollow out specific points at rest and the coverage sentence carries the fact.
    droppedIds: [],
    lenses: summary.lenses.map(lens => ({
      lens: lens.lens,
      state: lens.failed ? 'failed' : 'done',
      proposed: lens.proposed,
    })),
    pool: { raw: summary.candidates.raw, unique: summary.candidates.unique, merged: summary.candidates.merged },
    hypotheses: summary.verdicts.map(record => ({
      id: record.id,
      lenses: record.lenses,
      findingIds: record.findingIds,
      findings: record.findings.map(finding => ({
        findingId: finding.findingId,
        rule: finding.rule,
        path: finding.path,
        title: finding.title,
        tool: finding.tool,
      })),
      hypothesis: record.hypothesis,
      verdict: toVerdict(record.verdict),
      reason: record.reason,
      title: record.title,
      severity: record.severity,
    })),
    grounding: summary.grounding
      ? {
          chainsIn: summary.grounding.chainsIn,
          chainsOut: summary.grounding.chainsOut,
          referencesRepaired: summary.grounding.referencesRepaired,
          referencesDropped: summary.grounding.referencesDropped,
        }
      : undefined,
    threatModelGeneratedAt: summary.threatModelGeneratedAt,
    correlationSeconds: summary.phaseDurations.find(duration => duration.phase === 'correlating')?.seconds,
  }
}

export const droppedForBudget = (run: RunState): number =>
  run.indexed === undefined || run.totalFindings === undefined
    ? run.droppedIds.length
    : Math.max(run.totalFindings - run.indexed, run.droppedIds.length)

export const countVerdicts = (run: RunState): Record<HypothesisVerdict, number> =>
  run.hypotheses.reduce<Record<HypothesisVerdict, number>>(
    (counts, hypothesis) => ({ ...counts, [hypothesis.verdict]: counts[hypothesis.verdict] + 1 }),
    { pending: 0, verifying: 0, confirmed: 0, rejected: 0, errored: 0 }
  )

export const isVerifying = (run: RunState): boolean =>
  run.hypotheses.some(hypothesis => hypothesis.verdict === 'verifying')

/** Whether every dispatched finding was considered and no lens failed, i.e. an empty result is a full "no". */
export const isFullyCovered = (run: RunState): boolean =>
  droppedForBudget(run) === 0 && !run.lenses.some(lens => lens.state === 'failed')

export const LENS_LABELS: Record<string, string> = {
  flat: 'Flat lens',
  sink_first: 'Sink-first lens',
  capability: 'Capability lens',
}

export const lensLabel = (lens: string): string => LENS_LABELS[lens] ?? `${lens} lens`

/** What to call a finding: its title, else its rule, else where it is, else its bare id. */
export const runFindingLabel = (finding: RunFinding): string =>
  finding.title ?? (finding.rule || finding.path || finding.findingId)
